import { Map } from "immutable";
import { createSelector } from "reselect";
import { types } from "~/components/Chat/Hub/ConversationContent/types/index.jsx";
import { getConversationName, getGroupConversationDisplayName } from "~/components/Chat/Hub/util.jsx";
import { descSortCompare, sortCompare } from "~/utils/utils.jsx";
import {
  getAllConversationMessages,
  getMapOfUnreadMessagesByConversationId,
} from "./selector.conversationMessages.jsx";
import { getLoggedUserTeams } from "./selector.teams.jsx";
import { getActiveUsersSortedBy, getAllUsers, getLoggedUserId } from "./selector.users.jsx";

const getAllConversations = createSelector(
  (state) => state?.conversations,
  (conversations = Map()) => conversations
);

export const getObjectConversations = createSelector(getActiveUsersSortedBy, getLoggedUserTeams, (users, teams) => {
  const result = [];

  users
    .filter((user) => user.class !== "Client")
    .forEach((user) => {
      result.push({
        id: user.get("id"),
        type: "User",
        name: user.get("display_name"),
      });
    });

  teams.forEach((team) => {
    result.push({
      id: team.id,
      type: "Team",
      name: team.name,
    });
  });

  return result.sort((a, b) => sortCompare(a.name.toLowerCase(), b.name.toLowerCase()));
});

export const makeGetConversationById = (id) =>
  createSelector(getAllConversations, (conversations) => conversations?.get(`${id}`)?.toJS());

const getMapOfConversationRecentTimes = createSelector(getAllConversationMessages, (messages) => {
  const result = {};

  messages.forEach((message) => {
    let id = message.get("conversation_id");
    if (!result[id] || result[id] < message.get("created_on")) {
      result[id] = message.get("created_on");
    }
  });

  return result;
});

export const getRecentConversations = createSelector(
  getAllConversations,
  getMapOfConversationRecentTimes,
  getLoggedUserId,
  (state, props) => props?.draftTimestamps,
  getAllUsers,
  (conversations, mapOfRecentTimes, loggedUserId, draftTimestampsCollection = {}, users) => {
    return conversations
      .filter((conversation) => {
        return (
          conversation.get("members", []).some((userId) => userId === loggedUserId) &&
          (draftTimestampsCollection[`${conversation.get("id")}`] ||
            !!mapOfRecentTimes[`${conversation.get("id")}`] ||
            types[conversation.get("class")] === "GroupConversation" ||
            types[conversation.get("class")] === "GeneralConversation")
        );
      })
      .toArray()
      .sort((a, b) => {
        const firstMessageOrDraftTime =
          draftTimestampsCollection[`${a.get("id")}`] > mapOfRecentTimes[`${a?.get("id")}`]
            ? draftTimestampsCollection[`${a.get("id")}`]
            : mapOfRecentTimes[`${a?.get("id")}`] || draftTimestampsCollection[`${a.get("id")}`];
        const firstValue = firstMessageOrDraftTime || a?.get("created_on") || 0;

        const secondMessageOrDraftTime =
          draftTimestampsCollection[`${b.get("id")}`] > mapOfRecentTimes[`${b?.get("id")}`]
            ? draftTimestampsCollection[`${b.get("id")}`]
            : mapOfRecentTimes[`${b?.get("id")}`] || draftTimestampsCollection[`${b.get("id")}`];
        const secondValue = secondMessageOrDraftTime || b?.get("created_on") || 0;

        return descSortCompare(firstValue, secondValue);
      })
      .map((conversation) => {
        const conversationObject = conversation.toJS();
        const { members = [], class: objectType } = conversationObject;
        const type = types[objectType];

        const name = getConversationName(conversationObject, users.toJS(), loggedUserId);
        let searchName = name;
        if (type === "GroupConversation" && !conversationObject.name) {
          const conversationMembersWithoutLoggedUser = members
            .filter((memberId) => memberId !== loggedUserId)
            .reduce((acc, memberId) => {
              const user = users.get(`${memberId}`);
              if (user) {
                acc.push(user);
              }
              return acc;
            }, []);
          searchName = getGroupConversationDisplayName("", conversationMembersWithoutLoggedUser, true);
        }

        return {
          ...conversationObject,
          name,
          searchName,
        };
      });
  }
);

export const getConversations = createSelector(
  getObjectConversations,
  getRecentConversations,
  getLoggedUserId,
  (others, recentConversations, loggedUserId) => {
    let excludedUserIds = [];
    let excludedTeamIds = [];

    for (let i = 0; i < recentConversations.length; i++) {
      const recentConversation = recentConversations[i];
      const type = types[recentConversation?.class];

      if (type === "OneToOneConversation") {
        excludedUserIds.push(recentConversation.members.find((id) => id !== loggedUserId));
        continue;
      }

      if (type === "ParentObjectConversation") {
        if (recentConversation?.parent_type.toLowerCase() === "team" && recentConversation?.parent_id > 0) {
          excludedTeamIds.push(recentConversation.parent_id);
        }
        continue;
      }

      if (type === "OwnConversation") {
        excludedUserIds.push(loggedUserId);
      }
    }

    return others.filter((other) => {
      if (other?.type.toLowerCase() === "team") {
        return !excludedTeamIds.includes(other.id);
      }
      if (other?.type.toLowerCase() === "user") {
        return !excludedUserIds.includes(other.id);
      }
      return false;
    });
  }
);

export const makeGetConversationParticipants = (id) => {
  const getConversationById = makeGetConversationById(id);
  return createSelector(
    (state) => getConversationById(state)?.members,
    (state) => getConversationById(state)?.admins,
    getAllUsers,
    (conversationMembers = [], conversationAdmins = [], users) => {
      let admins;
      let participants;

      const getUser = (user) => ({
        id: user.get("id"),
        name: user.get("display_name"),
        avatarUrl: user.get("avatar_url"),
        isOwner: user.get("class") === "Owner",
      });

      admins = conversationAdmins
        .reduce((acc, adminId) => {
          const user = users.get(`${adminId}`);
          if (user) {
            acc.push(getUser(user));
          }
          return acc;
        }, [])
        .sort((a, b) => sortCompare(a?.name?.toLowerCase(), b?.name?.toLowerCase()));

      participants = conversationMembers
        .reduce((acc, memberId) => {
          const user = users.get(`${memberId}`);
          if (conversationAdmins.indexOf(memberId) === -1 && user) {
            acc.push(getUser(user));
          }
          return acc;
        }, [])
        .sort((a, b) => sortCompare(a?.name?.toLowerCase(), b?.name?.toLowerCase()));

      return {
        admins,
        participants,
      };
    }
  );
};

export const makeGetShortcutConversations = (excludedIds = []) => {
  return createSelector(
    getAllConversations,
    (state) => state.user_conversations,
    getMapOfUnreadMessagesByConversationId,
    getMapOfConversationRecentTimes,
    getLoggedUserId,
    getAllUsers,
    (conversations, userConversations, mapOfCounts, mapOfRecentTimes, loggedUserId, users) => {
      const filteredConversations = conversations
        .filter((conversation) => {
          return conversation.get("members", []).some((userId) => userId === loggedUserId);
        })
        .reduce((acc, conversation) => {
          const conversationId = conversation.get("id");
          const userConversation = userConversations.get(`${conversationId}`, Map());
          const isConversationMuted = userConversation.get("is_muted", false);
          const hasUnreadMessages = mapOfCounts[conversationId] > 0;
          const isExcluded = excludedIds.some((excludeId) => conversationId === excludeId);

          if (!isConversationMuted && !isExcluded && hasUnreadMessages) {
            const name = getConversationName(conversation.toJS(), users.toJS(), loggedUserId);
            acc.push({ ...conversation.toJS(), name });
          }

          return acc;
        }, [])
        .sort((a, b) => {
          const firstValue = mapOfRecentTimes[a.id] || 0;
          const secondValue = mapOfRecentTimes[b.id] || 0;
          return descSortCompare(firstValue, secondValue);
        });

      if (filteredConversations.length > 9) {
        return filteredConversations.slice(0, 9);
      }
      return filteredConversations;
    }
  );
};

export const makeGetShortcutConversationIds = (excludedIds = []) => {
  const getShortcutConversations = makeGetShortcutConversations(excludedIds);
  return createSelector(getShortcutConversations, (shortcutConversations) => {
    return shortcutConversations.map((conversation) => conversation.id);
  });
};

export const getOneToOneConversationIdByUserId = createSelector(
  getLoggedUserId,
  getAllConversations,
  (state, userId) => userId,
  (loggedUserId, conversations, userId) => {
    const conversation = conversations.find((conversation) => {
      return (
        types[conversation.get("class")] === "OneToOneConversation" &&
        conversation.get("members").includes(loggedUserId) &&
        conversation.get("members").includes(userId)
      );
    });

    return conversation ? conversation.get("id") : null;
  }
);
